import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Settings, Factory, CheckCircle2, Layers, Zap, Maximize2, Weight, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { ProductionLine, ProductCategory, CompanyStats, ROUTE_PATHS } from "@/lib/index";
import { cn } from "@/lib/utils";
import { springPresets, hoverLift, fadeInUp } from "@/lib/motion";

/**
 * Ekstrüzyon hatları için tasarlanmış teknik kart bileşeni.
 */
export function ProductionLineCard({
  line,
  language = 'tr'
}: {
  line: ProductionLine;
  language?: 'tr' | 'en';
}) {
  return <motion.div variants={fadeInUp} whileHover={hoverLift.hover as any} className="group relative bg-card border border-border rounded-2xl overflow-hidden flex flex-col h-full shadow-sm hover:shadow-xl transition-all duration-300">
      <div className="aspect-[16/10] overflow-hidden relative">
        <img src={line.image} alt={line.title} className="w-full transition-transform duration-700 group-hover:scale-110 h-[253.59573364257812px] object-cover" />
        
        <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-mono tracking-wider flex items-center gap-2">
          <Factory className="w-3 h-3" />
          {language === 'en' ? 'EXTRUSION' : 'EKSTRÜZYON'}
        </div>
      </div>

      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
          {line.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {line.description}
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6 py-4 border-y border-border/50">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              {language === 'en' ? 'Capacity' : 'Kapasite'}
            </span>
            <span className="font-mono font-bold text-primary flex items-center gap-1">
              <Weight className="w-3 h-3" />
              {line.capacity}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              {language === 'en' ? 'Technology' : 'Teknoloji'}
            </span>
            <span className="font-mono font-bold text-primary flex items-center gap-1">
              <Settings className="w-3 h-3" />
              PLC-V5
            </span>
          </div>
        </div>

        <ul className="space-y-2 mb-6">
          {line.specifications.slice(0, 3).map((spec, idx) => <li key={idx} className="flex items-start gap-2 text-xs text-foreground/80">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              <span>{spec}</span>
            </li>)}
        </ul>


      </div>
    </motion.div>;
}

/**
 * Yüzey işlem (Toz boya) tesisleri için kart bileşeni.
 */
export function CoatingFacilityCard({
  facility,
  language = 'tr'
}: {
  facility: ProductionLine;
  language?: 'tr' | 'en';
}) {
  return <motion.div variants={fadeInUp} whileHover={hoverLift.hover as any} className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col md:flex-row h-full group">
      <div className="md:w-1/3 relative">
        <img src={facility.image} alt={facility.title} className="w-full h-full object-cover min-h-[200px]" />
        <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
      </div>
      <div className="p-8 md:w-2/3 flex flex-col">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Layers className="w-5 h-5" />
            </div>
            <span className="text-xs uppercase font-bold text-primary tracking-wider">{language === 'en' ? 'POWDER COATING FACILITY' : 'TOZ BOYA TESİSİ'}</span>
          </div>
          <h3 className="text-xl font-bold text-foreground leading-tight">{facility.title}</h3>
        </div>
        
        <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
          {facility.description}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {facility.specifications.map((spec, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-foreground/70">
              <Zap className="w-4 h-4 text-primary" />
              {spec}
            </div>)}
        </div>

        <div className="mt-auto flex items-center gap-4 pt-4 border-t border-border/50">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase font-bold text-muted-foreground">Yıllık Kapasite</span>
            <span className="text-lg font-mono font-bold text-primary">{facility.capacity}</span>
          </div>
          <button className="ml-auto text-primary font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            Süreç Bilgisi <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>;
}

/**
 * Ürün kategorileri için mega-menu uyumlu kart bileşeni.
 */
export function ProductCard({
  category
}: {
  category: ProductCategory;
}) {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  const isTurkish = location.pathname.startsWith('/tr');
  const isSpanish = location.pathname.startsWith('/es');

  const getProductLink = () => {
    if (category.id === 'architectural') {
      if (isEnglish) return ROUTE_PATHS.ARCHITECTURAL_SYSTEMS_EN;
      if (isSpanish) return ROUTE_PATHS.ARCHITECTURAL_SYSTEMS_ES;
      return '/tr/mimari-sistemler';
    } else if (category.id === 'industrial') {
      if (isEnglish) return ROUTE_PATHS.INDUSTRIAL_PROFILES_EN;
      if (isSpanish) return ROUTE_PATHS.INDUSTRIAL_PROFILES_ES;
      return '/tr/endustriyel-profiller';
    } else if (category.id === 'facade') {
      if (isEnglish) return ROUTE_PATHS.FACADE_SYSTEMS_EN;
      if (isSpanish) return ROUTE_PATHS.FACADE_SYSTEMS_ES;
      return '/tr/cephe-dograma-sistemleri';
    }
    return category.path;
  };
  return <motion.div variants={fadeInUp} whileHover={hoverLift.hover as any} className="group relative h-[450px] rounded-3xl overflow-hidden bg-primary">
      <img src={category.image} alt={category.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/20 to-transparent" />
      
      <div className="absolute inset-0 p-8 flex flex-col justify-end">
        <div className="mb-2">
          <span className="text-white/70 text-xs font-mono uppercase tracking-[0.2em]">
            Ürün Grubu
          </span>
          <h3 className="text-3xl font-bold text-white mb-3">
            {category.title}
          </h3>
        </div>
        
        <p className="text-white/80 text-sm mb-6 line-clamp-2 max-w-sm">
          {category.description}
        </p>

        <ul className="space-y-2 mb-8">
          {category.features.slice(0, 3).map((feature, idx) => <li key={idx} className="flex items-center gap-3 text-white/90 text-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-accent" />
              {feature}
            </li>)}
        </ul>

        <Link to={getProductLink()} className="inline-flex items-center justify-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-bold hover:bg-accent hover:text-white transition-colors w-fit">
          {isEnglish ? 'Explore' : 'İncele'}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>;
}

/**
 * Kurumsal veriler için animasyonlu sayaç kartı.
 */
export function StatsCard({
  stat
}: {
  stat: CompanyStats;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    y: -8
  }} transition={springPresets.snappy} className="relative p-8 bg-white border border-border rounded-2xl flex flex-col items-center text-center group" style={{
    boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 8%, transparent)"
  }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-primary rounded-b-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      
      <div className="mb-4 text-4xl font-black font-mono tracking-tighter text-primary flex items-baseline">
        {stat.value}
        {stat.suffix && <span className="text-xl ml-1">{stat.suffix}</span>}
      </div>

      <div className="text-sm font-bold text-foreground uppercase tracking-widest mb-3">
        {stat.label}
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        {stat.description}
      </p>

      <div className="mt-6 p-2 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
        <Maximize2 className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </motion.div>;
}